import {
    collection,
    getDocs,
    query
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import moment from 'moment';
import { getFestivals } from './festivalService';

const EMPLOYEES_COLLECTION = 'employees';

const getCollectionName = (month, year) => {
    const m = String(month).padStart(2, '0');
    const y = year || new Date().getFullYear();
    return `attendance_${y}_${m}`;
};

/**
 * Get employee report
 */
export const getEmployeeReport = async (filters = {}) => {
    try {
        console.log('[ReportsService] Fetching employee report with filters:', filters);
        const usersRef = collection(db, EMPLOYEES_COLLECTION);
        const q = query(usersRef);

        const snapshot = await getDocs(q);
        console.log('[ReportsService] Found', snapshot.size, 'employees');
        let employees = [];

        snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            
            // Skip deleted employees
            if (data.isDeleted === true) return;
            
            // Handle both nested (personal/job) and flat structures
            const personal = data.personal || {};
            const job = data.job || {};
            
            const name = personal.fullName || 
                `${personal.firstName || data.firstName || ''} ${personal.lastName || data.lastName || ''}`.trim() ||
                'Unknown';
            
            employees.push({
                id: docSnap.id,
                employeeId: job.employeeCode || data.employeeId || docSnap.id,
                name: name,
                email: personal.email || data.email || '',
                department: job.department || data.department || '',
                designation: job.designation || data.designation || '',
                joiningDate: job.joiningDate || data.joiningDate?.toDate?.()?.toISOString() || data.joiningDate || '',
                phone: personal.phone || data.phone || '',
                status: data.status || (data.isActive !== false ? 'Active' : 'Inactive')
            });
        });

        console.log('[ReportsService] Processed', employees.length, 'employees');

        // Client-side filtering
        if (filters.department) {
            employees = employees.filter(e => e.department === filters.department);
        }
        if (filters.status && filters.status !== 'All') {
            employees = employees.filter(e => e.status === filters.status);
        } else if (!filters.status) {
            // Default to Active only
            employees = employees.filter(e => e.status === 'Active');
        }

        return {
            success: true,
            data: employees
        };
    } catch (error) {
        console.error('Error fetching employee report:', error);
        return { success: true, data: [] };
    }
};

/**
 * Parse month filter - handles both "YYYY-MM" string and separate month/year
 */
const parseMonthFilter = (filters) => {
    let month, year;
    
    if (filters.month && typeof filters.month === 'string' && filters.month.includes('-')) {
        // Format: "2024-12"
        const [y, m] = filters.month.split('-');
        year = parseInt(y);
        month = parseInt(m);
    } else {
        month = parseInt(filters.month) || new Date().getMonth() + 1;
        year = parseInt(filters.year) || new Date().getFullYear();
    }
    
    return { month, year };
};

/**
 * Get attendance report (dynamic collection) - Aggregated by employee
 */
export const getAttendanceReport = async (filters = {}) => {
    try {
        const { month, year } = parseMonthFilter(filters);
        const collectionName = getCollectionName(month, year);
        console.log('[ReportsService] Fetching attendance from collection:', collectionName);
        console.log('[ReportsService] Parsed month:', month, 'year:', year, 'from filters:', filters);

        // First, fetch all employees to get department info and status
        const employeeInfoMap = {};
        try {
            const employeesRef = collection(db, EMPLOYEES_COLLECTION);
            const empSnapshot = await getDocs(query(employeesRef));
            empSnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                
                // Skip deleted employees
                if (data.isDeleted === true) return;
                
                // Handle multiple data structures (personalInfo/jobDetails OR personal/job OR flat)
                const job = data.job || data.jobDetails || {};
                const empCode = job.employeeCode || data.employeeCode || data.employeeId || docSnap.id;
                const department = job.department || data.department || '';
                const isActive = data.status === 'Active' || (data.isActive !== false && data.status !== 'Inactive');
                
                const fullName = data.personal?.fullName || data.personalInfo?.fullName || data.fullName || 
                               `${data.firstName || data.personal?.firstName || ''} ${data.lastName || data.personal?.lastName || ''}`.trim();
                
                const empInfo = { 
                    department, 
                    isActive,
                    uid: docSnap.id,
                    employeeId: data.employeeId || '',
                    empCode: job.employeeCode || data.employeeCode || '',
                    fullName: fullName
                };
                
                // Map by various possible IDs
                employeeInfoMap[empCode] = empInfo;
                employeeInfoMap[String(empCode)] = empInfo;
                employeeInfoMap[docSnap.id] = empInfo;
                if (data.employeeId) employeeInfoMap[data.employeeId] = empInfo;
                if (data.authUserId) employeeInfoMap[data.authUserId] = empInfo;
            });
            console.log('[ReportsService] Loaded employee info for', Object.keys(employeeInfoMap).length, 'employees');
        } catch (e) {
            console.warn('Could not fetch employee info:', e);
        }

        let festivalSet = new Set();
        try {
            const festList = await getFestivals();
            festivalSet = new Set((festList || []).map(f => moment(f.date).format('YYYY-MM-DD')));
        } catch (e) {
            console.warn('Could not fetch festivals in reportsService:', e);
        }

        const rawRecords = [];

        // Helper for status detection (Matches AttendanceTable.jsx logic)
        const getEffectiveStatus = (r) => {
            if (!r) return 'Absent';

            // Parse date for Weekly Off check (Tuesday = 2)
            let dateObj = r.date instanceof Date ? r.date : (r.date?.toDate ? r.date.toDate() : new Date(r.date));
            const dayOfWeek = isNaN(dateObj.getTime()) ? -1 : dateObj.getDay();

            let dateKey = null;
            if (r.date && r.date._seconds) dateKey = moment.unix(r.date._seconds).format('YYYY-MM-DD');
            else if (r.date && r.date.seconds) dateKey = moment.unix(r.date.seconds).format('YYYY-MM-DD');
            else if (dateObj && !isNaN(dateObj.getTime())) dateKey = moment(dateObj).format('YYYY-MM-DD');

            const isFestivalDate = Boolean(dateKey && festivalSet.has(dateKey));

            // Punch Detection
            const hasPunch = (v) => v && v !== '-' && v !== '' && v !== '00:00' && v !== '0:00' && /[1-9]/.test(String(v));
            const hasIn = hasPunch(r.inTime);
            const hasOut = hasPunch(r.outTime);

            const s = (r.status || '').toLowerCase();

            // 1. Explicit Overrides
            if (s.includes('leave')) return 'Leave';
            if (s.includes('half')) return 'Half Day';
            if (s.includes('holiday')) return 'Holiday';
            if (s.includes('festival')) return 'Festival';

            // 2. Tuesday Rule (Weekly Off)
            if (dayOfWeek === 2 && !hasIn && !hasOut) return 'Weekly Off';

            // 3. Festival Rule (Except Tuesday): If festival day and no punches or status is Absent -> Festival (paid leave)
            if (isFestivalDate && dayOfWeek !== 2 && ((!hasIn && !hasOut) || s.includes('absent'))) {
                return 'Festival';
            }

            // 4. Miss Punch Rule
            if ((hasIn && !hasOut) || (!hasIn && hasOut)) return 'Miss Punch';

            // 5. Absent Rule
            if (!hasIn && !hasOut) return dayOfWeek === 2 ? 'Weekly Off' : 'Absent';

            // 6. Late / Early Exit detection
            const late = String(r.lateBy || '').trim();
            if (late && late !== '00:00' && late !== '0:00' && late !== '-') return 'Late';

            const early = String(r.earlyGoingBy || '').trim();
            if (early && early !== '00:00' && early !== '0:00' && early !== '-') return 'Early Exit';

            return 'Present';
        };

        // Try the monthly collection first (attendance_YYYY_MM)
        try {
            const attendanceRef = collection(db, collectionName);
            const snapshot = await getDocs(query(attendanceRef));
            console.log('[ReportsService] Found', snapshot.size, 'attendance records in', collectionName);
            snapshot.forEach((doc) => {
                const data = doc.data();
                rawRecords.push({
                    docId: doc.id,
                    ...data,
                    date: data.date?.toDate?.() || data.date
                });
            });
        } catch (e) {
            console.warn(`Collection ${collectionName} likely does not exist yet.`, e);
        }

        // If no records found, also check the generic 'attendance' collection (legacy/sample data)
        if (rawRecords.length === 0) {
            try {
                const legacyRef = collection(db, 'attendance');
                const legacySnapshot = await getDocs(query(legacyRef));
                
                legacySnapshot.forEach((doc) => {
                    const data = doc.data();
                    const recordDate = data.date ? (data.date.toDate ? data.date.toDate() : new Date(data.date)) : null;
                    
                    if (recordDate && recordDate.getMonth() + 1 === month && recordDate.getFullYear() === year) {
                        rawRecords.push({
                            docId: doc.id,
                            ...data,
                            date: recordDate
                        });
                    }
                });
            } catch (e) {
                console.warn('Legacy attendance collection not accessible:', e);
            }
        }
        
        // Calculate actual working days excluding Tuesdays (Weekly Off)
        const getWorkingDaysCount = (m, y) => {
            const daysInMonth = new Date(y, m, 0).getDate();
            let count = 0;
            for (let d = 1; d <= daysInMonth; d++) {
                const date = new Date(y, m - 1, d);
                if (date.getDay() !== 2) { // 2 = Tuesday
                    count++;
                }
            }
            return count;
        };

        const totalWorkingDays = getWorkingDaysCount(month, year);
        console.log(`[ReportsService] Calculated ${totalWorkingDays} working days for ${month}/${year} (Excluding Tuesdays)`);

        // Aggregate by employee
        const employeeStats = {};

        rawRecords.forEach(record => {
            const empId = record.employeeId || record.empCode || record.uid;
            if (!empId) return;

            // Look up employee info
            let empInfo = employeeInfoMap[empId] || employeeInfoMap[String(empId)] || {};
            
            // Fallback: If not found by ID, try matching by name
            if (!empInfo.uid) {
                const recName = (record.employeeName || record.empName || '').toLowerCase().trim();
                if (recName) {
                    for (const key in employeeInfoMap) {
                        const info = employeeInfoMap[key];
                        const infoName = (info.fullName || '').toLowerCase().trim();
                        // Allow slight variations like "Chowgule" vs "Chougule"
                        if (infoName === recName || 
                            infoName.replace(/w/g, 'u') === recName.replace(/w/g, 'u')) {
                            empInfo = info;
                            break;
                        }
                    }
                }
            }
            
            // Skip inactive employees
            if (empInfo.isActive === false) return;
            
            if (!employeeStats[empId]) {
                employeeStats[empId] = {
                    employeeId: empInfo.employeeId || empInfo.empCode || empId,
                    firebaseUid: empInfo.uid || empId,
                    systemEmployeeId: empInfo.employeeId || '',
                    systemEmpCode: empInfo.empCode || '',
                    employeeName: record.employeeName || record.empName || 'Unknown',
                    department: empInfo.department || record.department || '',
                    totalWorkingDays: totalWorkingDays,
                    presentDays: 0,
                    absentDays: 0,
                    lateMarks: 0,
                    halfDays: 0,
                    missPunch: 0,
                    weeklyOffs: 0,
                    overtimeHours: 0
                };
            }

            const stats = employeeStats[empId];
            const effectiveStatus = getEffectiveStatus(record);
            
            switch (effectiveStatus) {
                case 'Present':
                case 'Festival':
                    stats.presentDays++;
                    break;
                case 'Absent':
                case 'Leave':
                    stats.absentDays++;
                    break;
                case 'Late':
                    stats.presentDays++;
                    stats.lateMarks++;
                    break;
                case 'Half Day':
                    stats.presentDays += 0.5;
                    stats.halfDays++;
                    stats.lateMarks++;
                    break;
                case 'Miss Punch':
                    stats.presentDays++;
                    stats.missPunch++;
                    break;
                case 'Weekly Off':
                    stats.weeklyOffs++;
                    break;
                case 'Early Exit':
                    stats.presentDays++;
                    break;
                default:
                    break;
            }

            // Also accumulate overtime if present
            const ot = parseFloat(record.overtimeHours || record.overtime || 0);
            if (!isNaN(ot)) stats.overtimeHours += ot;
        });

        // Convert to array and calculate attendance percentage
        let records = Object.values(employeeStats).map(emp => ({
            ...emp,
            attendancePercentage: emp.totalWorkingDays > 0 
                ? Math.round((emp.presentDays / emp.totalWorkingDays) * 100) 
                : 0
        }));

        // Client-side filtering by employee
        // Filter by employee name if provided (for admin search)
        if (filters.employeeName && filters.employeeName.trim() !== '') {
            const nameFilter = filters.employeeName.toLowerCase().trim();
            records = records.filter(r => 
                (r.employeeName || '').toLowerCase().includes(nameFilter)
            );
            console.log('[ReportsService] After name filter:', records.length, 'records');
        }
        
        // Filter by employee ID/Code/UID - include all provided values
        let filterValues = [];
        [filters.employeeId, filters.employeeCode, filters.employeeUid].forEach(v => {
            if (v && typeof v === 'string' && v.trim() !== '') {
                const parts = v.split(',').map(p => p.trim().toLowerCase()).filter(p => p !== '');
                filterValues = [...filterValues, ...parts];
            } else if (v && !isNaN(v)) {
                filterValues.push(String(v).toLowerCase().trim());
            }
        });
        
        // STRICT ID-based filtering for employees - no name matching to prevent data leakage
        if (filterValues.length > 0) {
            records = records.filter(r => {
                const eId = (r.employeeId || '').toLowerCase().trim();
                const fUid = (r.firebaseUid || '').toLowerCase().trim();
                const sEId = (r.systemEmployeeId || '').toLowerCase().trim();
                const sECode = (r.systemEmpCode || '').toLowerCase().trim();
                // STRICT match - must exactly match one of the filter values
                return filterValues.some(fv => eId === fv || fUid === fv || sEId === fv || sECode === fv);
            });
            console.log('[ReportsService] After STRICT employeeId filter:', records.length, 'records');
        }

        // Filter by department if provided
        if (filters.department && filters.department.trim() !== '') {
            records = records.filter(r => r.department === filters.department);
            console.log('[ReportsService] After department filter:', records.length, 'records');
        }

        // Sort by employee name
        records.sort((a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''));

        console.log('[ReportsService] Returning', records.length, 'aggregated attendance records');
        return {
            success: true,
            data: records,
            rawRecords: rawRecords
        };
    } catch (error) {
        console.error('Error fetching attendance report:', error);
        return { success: true, data: [] };
    }
};

/**
 * Get leave report - Aggregated by employee
 */
export const getLeaveReport = async (filters = {}) => {
    try {
        console.log('[ReportsService] Fetching leave report with filters:', filters);
        
        // First, fetch all employees to get department and employee code info
        const employeeInfoMap = {};
        try {
            const employeesRef = collection(db, EMPLOYEES_COLLECTION);
            const empSnapshot = await getDocs(query(employeesRef));
            empSnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                
                // Handle ALL possible data structures
                const jobDetails = data.jobDetails || {};
                const job = data.job || {};
                const personalInfo = data.personalInfo || {};
                const personal = data.personal || {};
                
                const isInactive = (data.status || '').toLowerCase() === 'inactive' || data.isActive === false;

                // Get employee code from all possible locations
                const empCode = data.employeeCode || 
                    jobDetails.employeeCode || 
                    job.employeeCode || 
                    data.employeeId || 
                    docSnap.id;
                
                // Get department from all possible locations
                const department = jobDetails.department || 
                    job.department || 
                    data.department || 
                    '';
                
                // Get employee name from all possible locations
                const empName = personalInfo.fullName ||
                    personal.fullName ||
                    data.fullName ||
                    `${personalInfo.firstName || personal.firstName || data.firstName || ''} ${personalInfo.lastName || personal.lastName || data.lastName || ''}`.trim() ||
                    '';
                
                const empInfo = { empCode, department, empName, isActive: !isInactive };

                // Map by Firebase UID (docSnap.id) since leave records use UID as employeeId
                employeeInfoMap[docSnap.id] = empInfo;
                // Also map by employeeCode for flexibility
                if (empCode) {
                    employeeInfoMap[empCode] = empInfo;
                    employeeInfoMap[String(empCode)] = empInfo;
                }
                if (data.employeeId) employeeInfoMap[data.employeeId] = empInfo;
                // Also map by name for fallback
                if (empName) employeeInfoMap[empName.toLowerCase()] = empInfo;
                
                console.log('[ReportsService] Mapped employee:', docSnap.id, '-> empCode:', empCode, 'dept:', department, 'name:', empName, 'isActive:', !isInactive);
            });
            console.log('[ReportsService] Loaded employee info for', Object.keys(employeeInfoMap).length, 'employees');
        } catch (e) {
            console.warn('Could not fetch employee info:', e);
        }
        
        const leavesRef = collection(db, 'wrms_leave_requests');
        const q = query(leavesRef);

        const snapshot = await getDocs(q);
        const rawLeaves = [];

        snapshot.forEach((doc) => {
            const data = doc.data();
            rawLeaves.push({
                employeeId: data.employeeId,
                employeeName: data.employeeName,
                status: data.status,
                leaveType: data.leaveType
            });
        });

        console.log('[ReportsService] Found', rawLeaves.length, 'leave records');

        // Aggregate by employee
        const employeeStats = {};

        rawLeaves.forEach(leave => {
            if (!employeeStats[leave.employeeId]) {
                // Look up employee info (code and department)
                // Try multiple lookup strategies
                let empInfo = employeeInfoMap[leave.employeeId];
                
                // If not found by ID, try by name (lowercase)
                if (!empInfo || !empInfo.empCode) {
                    const leaveName = (leave.employeeName || '').toLowerCase().trim();
                    if (leaveName && employeeInfoMap[leaveName]) {
                        empInfo = employeeInfoMap[leaveName];
                        console.log('[ReportsService] Found employee by name key:', leaveName);
                    }
                }
                
                // If still not found, try partial name match
                if (!empInfo || !empInfo.empCode) {
                    const leaveName = (leave.employeeName || '').toLowerCase().trim();
                    for (const [key, info] of Object.entries(employeeInfoMap)) {
                        const infoName = (info.empName || '').toLowerCase().trim();
                        if (infoName && leaveName && (infoName === leaveName || infoName.includes(leaveName) || leaveName.includes(infoName))) {
                            empInfo = info;
                            console.log('[ReportsService] Found employee by name match:', leaveName, '->', info);
                            break;
                        }
                    }
                }
                
                empInfo = empInfo || {};
                
                // Strictly skip leaves from inactive employees
                if (empInfo.isActive === false) return;

                console.log('[ReportsService] Leave lookup for:', leave.employeeId, leave.employeeName, 'found:', empInfo);
                
                employeeStats[leave.employeeId] = {
                    employeeId: empInfo.empCode || leave.employeeId, // Show empCode or original ID
                    firebaseUid: leave.employeeId, // Keep UID for filtering
                    employeeName: leave.employeeName || empInfo.empName || '',
                    department: empInfo.department || '',
                    totalLeaveBalance: 20, // Default annual leave balance
                    leavesTaken: 0,
                    pendingRequests: 0,
                    approvedLeaves: 0,
                    rejectedLeaves: 0
                };
            }

            const stats = employeeStats[leave.employeeId];
            
            switch (leave.status) {
                case 'approved':
                    stats.approvedLeaves++;
                    stats.leavesTaken++;
                    break;
                case 'pending':
                    stats.pendingRequests++;
                    break;
                case 'rejected':
                    stats.rejectedLeaves++;
                    break;
                default:
                    break;
            }
        });

        // Convert to array and calculate remaining balance
        let records = Object.values(employeeStats).map(emp => ({
            ...emp,
            remainingBalance: emp.totalLeaveBalance - emp.leavesTaken
        }));

        // Client-side filtering - check employeeId, employeeUid, and employeeCode
        let filterValues = [];
        [filters.employeeId, filters.employeeUid, filters.employeeCode].forEach(v => {
            if (v && typeof v === 'string' && v.trim() !== '') {
                const parts = v.split(',').map(p => p.trim().toLowerCase()).filter(p => p !== '');
                filterValues = [...filterValues, ...parts];
            } else if (v && !isNaN(v)) {
                filterValues.push(String(v).toLowerCase().trim());
            }
        });
        
        // STRICT ID-based filtering for employees - no name matching to prevent data leakage
        if (filterValues.length > 0) {
            records = records.filter(r => {
                const empId = (r.employeeId || '').toLowerCase().trim();
                const firebaseUid = (r.firebaseUid || '').toLowerCase().trim();
                // STRICT match - must exactly match one of the filter values
                return filterValues.some(fv => empId === fv || firebaseUid === fv);
            });
            console.log('[ReportsService] Leave: After STRICT ID filter:', records.length, 'records');
        }

        // Filter by department if provided
        if (filters.department && filters.department.trim() !== '') {
            records = records.filter(r => r.department === filters.department);
            console.log('[ReportsService] Leave: After department filter:', records.length, 'records');
        }

        // Sort by employee name
        records.sort((a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''));

        return {
            success: true,
            data: records
        };
    } catch (error) {
        console.error('Error fetching leave report:', error);
        return { success: true, data: [] };
    }
};

/**
 * Get payroll report
 */
export const getPayrollReport = async (filters = {}) => {
    try {
        const payrollRef = collection(db, 'payroll');
        const q = query(payrollRef);

        const snapshot = await getDocs(q);
        let records = [];

        snapshot.forEach((doc) => {
            const data = doc.data();
            const basicSalary = data.salaryStructure?.basic || data.basicSalary || 0;
            const hra = data.salaryStructure?.hra || data.hra || 0;
            const da = data.salaryStructure?.da || data.da || 0;
            const allowances = data.salaryStructure?.allowances || data.allowances || 0;
            const totalAllowances = hra + da + allowances;
            const deductions = data.deductions?.total || data.deductions || 0;
            const grossSalary = data.grossSalary || (basicSalary + totalAllowances);
            const netSalary = data.netSalary || (grossSalary - deductions);
            
            records.push({
                employeeId: data.employeeId,
                employeeName: data.employeeName,
                department: '',
                month: data.month,
                year: data.year,
                basicSalary: basicSalary,
                allowances: totalAllowances,
                grossSalary: grossSalary,
                deductions: deductions,
                netSalary: netSalary,
                paymentStatus: data.status || 'Paid',
                createdAt: data.createdAt?.toDate?.() || new Date()
            });
        });

        // Client-side filtering
        if (filters.employeeId) {
            records = records.filter(r => r.employeeId === filters.employeeId);
        }
        
        // Handle month filter (can be "YYYY-MM" string or separate month/year)
        if (filters.month) {
            const { month, year } = parseMonthFilter(filters);
            records = records.filter(r => r.month === month && r.year === year);
        } else if (filters.year) {
            records = records.filter(r => r.year === parseInt(filters.year));
        }

        // Filter by department if provided
        if (filters.department && filters.department.trim() !== '') {
            records = records.filter(r => r.department === filters.department);
        }

        // Client-side sorting
        records.sort((a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''));

        return {
            success: true,
            data: records
        };
    } catch (error) {
        console.error('Error fetching payroll report:', error);
        return { success: true, data: [] };
    }
};

/**
 * Get department-wise report
 */
export const getDepartmentReport = async () => {
    try {
        const usersRef = collection(db, EMPLOYEES_COLLECTION);
        const snapshot = await getDocs(query(usersRef));

        const departmentStats = {};

        snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            
            // Skip deleted employees
            if (data.isDeleted === true) return;

            // Handle both nested and flat structures
            const personal = data.personal || {};
            const job = data.job || {};
            const salary = data.salary || {};
            
            const dept = job.department || data.department || 'Unassigned';
            const isActive = data.status === 'Active' || (data.isActive !== false && data.status !== 'Inactive');
            const basicSalary = salary.basicSalary || data.basicSalary || 0;

            if (!departmentStats[dept]) {
                departmentStats[dept] = {
                    departmentName: dept,
                    totalEmployees: 0,
                    activeEmployees: 0,
                    inactiveEmployees: 0,
                    totalSalary: 0,
                    averageAttendance: 85 // Default placeholder
                };
            }

            departmentStats[dept].totalEmployees++;
            if (isActive) {
                departmentStats[dept].activeEmployees++;
            } else {
                departmentStats[dept].inactiveEmployees++;
            }
            departmentStats[dept].totalSalary += basicSalary;
        });

        // Convert to array and calculate averages
        const report = Object.values(departmentStats).map(dept => ({
            ...dept,
            averageSalary: dept.totalEmployees > 0 
                ? Math.round(dept.totalSalary / dept.totalEmployees) 
                : 0
        }));

        // Sort by department name
        report.sort((a, b) => a.departmentName.localeCompare(b.departmentName));

        return {
            success: true,
            data: report
        };
    } catch (error) {
        console.error('Error fetching department report:', error);
        return { success: true, data: [] };
    }
};

/**
 * Get overtime report - Aggregated by employee
 */
export const getOvertimeReport = async (filters = {}) => {
    try {
        const { month, year } = parseMonthFilter(filters);
        const collectionName = getCollectionName(month, year);

        const attendanceRef = collection(db, collectionName);
        const employeeStats = {};

        try {
            const snapshot = await getDocs(query(attendanceRef));
            snapshot.forEach((doc) => {
                const data = doc.data();
                const overtimeHours = data.overtimeHours || 0;
                
                if (overtimeHours > 0) {
                    if (!employeeStats[data.employeeId]) {
                        employeeStats[data.employeeId] = {
                            employeeId: data.employeeId,
                            employeeName: data.employeeName || '',
                            department: '',
                            totalOvertimeHours: 0,
                            overtimeDays: 0,
                            hourlyRate: 100, // Default hourly rate
                        };
                    }
                    employeeStats[data.employeeId].totalOvertimeHours += overtimeHours;
                    employeeStats[data.employeeId].overtimeDays++;
                }
            });
        } catch (e) { /* ignore missing collection */ }

        // Convert to array and calculate OT pay
        let records = Object.values(employeeStats).map(emp => ({
            ...emp,
            totalOvertimeHours: Math.round(emp.totalOvertimeHours * 100) / 100,
            overtimePay: Math.round(emp.totalOvertimeHours * emp.hourlyRate)
        }));

        // STRICT ID-based filtering for employees
        let filterValues = [];
        [filters.employeeId, filters.employeeCode, filters.employeeUid].forEach(v => {
            if (v && typeof v === 'string' && v.trim() !== '') {
                const parts = v.split(',').map(p => p.trim().toLowerCase()).filter(p => p !== '');
                filterValues = [...filterValues, ...parts];
            } else if (v && !isNaN(v)) {
                filterValues.push(String(v).toLowerCase().trim());
            }
        });
        
        if (filterValues.length > 0) {
            records = records.filter(r => {
                const empId = (r.employeeId || '').toLowerCase().trim();
                return filterValues.some(fv => empId === fv);
            });
            console.log('[ReportsService] Overtime: After STRICT ID filter:', records.length, 'records');
        }

        // Filter by department if provided
        if (filters.department && filters.department.trim() !== '') {
            records = records.filter(r => r.department === filters.department);
        }

        // Sort by total overtime hours descending
        records.sort((a, b) => b.totalOvertimeHours - a.totalOvertimeHours);

        return {
            success: true,
            data: records
        };
    } catch (error) {
        console.error('Error fetching overtime report:', error);
        return { success: true, data: [] };
    }
};

/**
 * Get late marks report - Aggregated by employee
 */
export const getLateMarksReport = async (filters = {}) => {
    try {
        const { month, year } = parseMonthFilter(filters);
        const collectionName = getCollectionName(month, year);

        const attendanceRef = collection(db, collectionName);
        const employeeStats = {};

        try {
            const snapshot = await getDocs(query(attendanceRef));
            snapshot.forEach((doc) => {
                const data = doc.data();
                // Check 'status' for late marks
                if (data.status === 'Late' || data.status === 'Half Day' || data.status === 'Half-day') {
                    if (!employeeStats[data.employeeId]) {
                        employeeStats[data.employeeId] = {
                            employeeId: data.employeeId,
                            employeeName: data.employeeName || '',
                            department: '',
                            lateMarks: 0,
                            dates: []
                        };
                    }
                    employeeStats[data.employeeId].lateMarks++;
                    employeeStats[data.employeeId].dates.push(data.dateString);
                }
            });
        } catch (e) { }

        // Convert to array and format date range
        let records = Object.values(employeeStats).map(emp => {
            const sortedDates = emp.dates.sort();
            return {
                employeeId: emp.employeeId,
                employeeName: emp.employeeName,
                department: emp.department,
                lateMarks: emp.lateMarks,
                dateRange: sortedDates.length > 0 
                    ? `${sortedDates[0]} to ${sortedDates[sortedDates.length - 1]}`
                    : 'N/A'
            };
        });

        // STRICT ID-based filtering for employees
        let filterValues = [];
        [filters.employeeId, filters.employeeCode, filters.employeeUid].forEach(v => {
            if (v && typeof v === 'string' && v.trim() !== '') {
                const parts = v.split(',').map(p => p.trim().toLowerCase()).filter(p => p !== '');
                filterValues = [...filterValues, ...parts];
            } else if (v && !isNaN(v)) {
                filterValues.push(String(v).toLowerCase().trim());
            }
        });
        
        if (filterValues.length > 0) {
            records = records.filter(r => {
                const empId = (r.employeeId || '').toLowerCase().trim();
                return filterValues.some(fv => empId === fv);
            });
            console.log('[ReportsService] LateMarks: After STRICT ID filter:', records.length, 'records');
        }

        // Filter by department if provided
        if (filters.department && filters.department.trim() !== '') {
            records = records.filter(r => r.department === filters.department);
        }

        // Sort by late marks descending
        records.sort((a, b) => b.lateMarks - a.lateMarks);

        return {
            success: true,
            data: records
        };
    } catch (error) {
        console.error('Error fetching late marks report:', error);
        return { success: true, data: [] };
    }
};

/**
 * Get employee movement report (joining/exit)
 */
export const getEmployeeMovementReport = async (filters = {}) => {
    try {
        const usersRef = collection(db, EMPLOYEES_COLLECTION);
        const snapshot = await getDocs(query(usersRef));

        const movements = [];
        snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            
            // Skip deleted employees
            if (data.isDeleted === true) return;
            
            // Handle both nested and flat structures
            const personal = data.personal || {};
            const job = data.job || {};
            
            const name = personal.fullName || 
                `${personal.firstName || data.firstName || ''} ${personal.lastName || data.lastName || ''}`.trim() ||
                'Unknown';
            const employeeId = job.employeeCode || data.employeeId || docSnap.id;
            const department = job.department || data.department || '';
            const designation = job.designation || data.designation || '';
            const joiningDate = job.joiningDate || data.joiningDate;
            const isActive = data.status === 'Active' || (data.isActive !== false && data.status !== 'Inactive');

            // Joining
            if (joiningDate) {
                const dateStr = joiningDate?.toDate?.()?.toISOString()?.split('T')[0] || 
                    (typeof joiningDate === 'string' ? joiningDate.split('T')[0] : '');
                movements.push({
                    employeeName: name,
                    employeeId: employeeId,
                    department: department,
                    designation: designation,
                    movementType: 'Joining',
                    date: dateStr,
                    status: isActive ? 'Active' : 'Inactive'
                });
            }

            // Exit (if inactive and has exit date)
            if (!isActive && data.exitDate) {
                const exitDateStr = data.exitDate?.toDate?.()?.toISOString()?.split('T')[0] || 
                    (typeof data.exitDate === 'string' ? data.exitDate.split('T')[0] : '');
                movements.push({
                    employeeName: name,
                    employeeId: employeeId,
                    department: department,
                    designation: designation,
                    movementType: 'Exit',
                    date: exitDateStr,
                    status: 'Inactive'
                });
            }
        });

        movements.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

        return {
            success: true,
            data: movements
        };
    } catch (error) {
        console.error('Error fetching employee movement report:', error);
        return { success: true, data: [] };
    }
};

/**
 * Get loan/advance report
 */
export const getLoanAdvanceReport = async (filters = {}) => {
    try {
        // This would require a loans collection in Firestore
        // For now, return empty data
        return {
            success: true,
            data: [],
            message: 'Loan/Advance feature not yet implemented in Firebase'
        };
    } catch (error) {
        console.error('Error fetching loan/advance report:', error);
        return { success: true, data: [] };
    }
};

/**
 * Get department-wise monthly attendance report
 * Shows attendance statistics grouped by department for a specific month
 */
export const getDepartmentWiseReport = async (filters = {}) => {
    try {
        const { month, year } = parseMonthFilter(filters);
        const collectionName = getCollectionName(month, year);
        console.log('[ReportsService] Fetching department-wise attendance from:', collectionName, 'filters:', filters);

        // First, fetch department names from departments collection
        const departmentNames = {};
        try {
            const deptRef = collection(db, 'departments');
            const deptSnapshot = await getDocs(deptRef);
            console.log('[ReportsService] Found', deptSnapshot.size, 'departments');
            deptSnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                if (!data.isDeleted) {
                    // Map code to name - use docSnap.id as code if data.code is missing
                    const code = data.code || docSnap.id;
                    departmentNames[code] = data.name || code;
                }
            });
            console.log('[ReportsService] Loaded department names:', departmentNames);
        } catch (e) {
            console.warn('Could not fetch department names:', e);
        }

        // Fetch all employees to get department info
        const employeeDeptMap = {};
        const departmentEmployees = {}; // Track employees per department (by code)
        
        try {
            const employeesRef = collection(db, EMPLOYEES_COLLECTION);
            const empSnapshot = await getDocs(employeesRef);
            console.log('[ReportsService] Found', empSnapshot.size, 'employees');
            empSnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                
                // Skip deleted and inactive employees
                if (data.isDeleted === true) return;
                const isInactive = (data.status || '').toLowerCase() === 'inactive' || data.isActive === false;
                if (isInactive) return;
                
                // Handle multiple data structures
                const job = data.job || data.jobDetails || {};
                const empCode = job.employeeCode || data.employeeCode || data.employeeId || docSnap.id;
                const deptCode = job.department || data.department || 'Unassigned';
                
                // Map employee to department code
                employeeDeptMap[empCode] = deptCode;
                employeeDeptMap[String(empCode)] = deptCode;
                employeeDeptMap[docSnap.id] = deptCode;
                if (data.employeeId) employeeDeptMap[data.employeeId] = deptCode;
                
                // Track employees per department
                if (!departmentEmployees[deptCode]) {
                    departmentEmployees[deptCode] = new Set();
                }
                departmentEmployees[deptCode].add(empCode);
            });
            console.log('[ReportsService] Loaded department info for employees, departments:', Object.keys(departmentEmployees));
        } catch (e) {
            console.warn('Could not fetch employee departments:', e);
        }

        // Fetch attendance records
        const rawRecords = [];
        
        try {
            const attendanceRef = collection(db, collectionName);
            const snapshot = await getDocs(attendanceRef);
            console.log('[ReportsService] Found', snapshot.size, 'attendance records in', collectionName);
            
            snapshot.forEach((docSnap) => {
                const data = docSnap.data();
                const empId = data.employeeId;
                const deptCode = employeeDeptMap[empId] || employeeDeptMap[String(empId)] || 'Unassigned';
                
                rawRecords.push({
                    employeeId: empId,
                    departmentCode: deptCode,
                    status: data.status
                });
            });
        } catch (e) {
            console.warn(`Collection ${collectionName} may not exist:`, e);
        }

        // Also check legacy attendance collection if no records found
        if (rawRecords.length === 0) {
            try {
                const legacyRef = collection(db, 'attendance');
                const legacySnapshot = await getDocs(query(legacyRef));
                console.log('[ReportsService] Checking legacy attendance, found', legacySnapshot.size, 'records');
                
                legacySnapshot.forEach((doc) => {
                    const data = doc.data();
                    const recordDate = data.date ? new Date(data.date) : null;
                    
                    if (recordDate && recordDate.getMonth() + 1 === month && recordDate.getFullYear() === year) {
                        const empId = data.userId || data.employeeId;
                        const deptCode = employeeDeptMap[empId] || employeeDeptMap[String(empId)] || 'Unassigned';
                        
                        rawRecords.push({
                            employeeId: empId,
                            departmentCode: deptCode,
                            status: data.status ? data.status.charAt(0).toUpperCase() + data.status.slice(1) : 'Present'
                        });
                    }
                });
                console.log('[ReportsService] Found', rawRecords.length, 'records for month', month, year);
            } catch (e) {
                console.warn('Legacy attendance collection not accessible:', e);
            }
        }

        // Calculate actual working days excluding Tuesdays (Weekly Off)
        const getWorkingDaysCount = (m, y) => {
            const daysInMonth = new Date(y, m, 0).getDate();
            let count = 0;
            for (let d = 1; d <= daysInMonth; d++) {
                const date = new Date(y, m - 1, d);
                if (date.getDay() !== 2) { // 2 = Tuesday
                    count++;
                }
            }
            return count;
        };

        const totalWorkingDays = getWorkingDaysCount(month, year);
        const departmentStats = {};

        // Initialize all departments from employee data
        Object.keys(departmentEmployees).forEach(deptCode => {
            departmentStats[deptCode] = {
                departmentCode: deptCode,
                departmentName: departmentNames[deptCode] || deptCode,
                totalEmployees: departmentEmployees[deptCode].size,
                totalWorkingDays: totalWorkingDays,
                presentDays: 0,
                absentDays: 0,
                lateMarks: 0,
                halfDays: 0,
                attendancePercentage: 0
            };
        });

        // Process attendance records
        rawRecords.forEach(record => {
            const deptCode = record.departmentCode;
            
            if (!departmentStats[deptCode]) {
                departmentStats[deptCode] = {
                    departmentCode: deptCode,
                    departmentName: departmentNames[deptCode] || deptCode,
                    totalEmployees: 0,
                    totalWorkingDays: totalWorkingDays,
                    presentDays: 0,
                    absentDays: 0,
                    lateMarks: 0,
                    halfDays: 0,
                    attendancePercentage: 0
                };
            }

            const stats = departmentStats[deptCode];
            
            switch (record.status) {
                case 'Present':
                    stats.presentDays++;
                    break;
                case 'Absent':
                case 'Leave':
                    stats.absentDays++;
                    break;
                case 'Late':
                    stats.presentDays++;
                    stats.lateMarks++;
                    break;
                case 'Half Day':
                case 'Half-day':
                    stats.halfDays++;
                    stats.lateMarks++;
                    break;
                default:
                    break;
            }
        });

        // Calculate attendance percentage for each department
        let records = Object.values(departmentStats).map(dept => {
            const totalPossibleDays = dept.totalEmployees * dept.totalWorkingDays;
            const totalPresent = dept.presentDays + dept.halfDays * 0.5;
            
            return {
                ...dept,
                attendancePercentage: totalPossibleDays > 0 
                    ? Math.round((totalPresent / totalPossibleDays) * 100) 
                    : 0
            };
        });

        console.log('[ReportsService] Before filter, records:', records.map(r => ({ code: r.departmentCode, name: r.departmentName })));
        console.log('[ReportsService] Filter department:', filters.department);

        // Filter by department code if specified
        if (filters.department && filters.department.trim() !== '') {
            records = records.filter(r => r.departmentCode === filters.department);
            console.log('[ReportsService] After department filter:', records.length, 'records');
        }

        // Sort by department name
        records.sort((a, b) => a.departmentName.localeCompare(b.departmentName));

        console.log('[ReportsService] Returning', records.length, 'department-wise attendance records');
        return {
            success: true,
            data: records
        };
    } catch (error) {
        console.error('Error fetching department-wise report:', error);
        return { success: true, data: [] };
    }
};

/**
 * Export report to Excel
 */
export const exportReport = async (reportType, data, filename) => {
    try {
        // This is handled client-side using xlsx library
        // Just return success
        return {
            success: true,
            message: 'Use client-side Excel export'
        };
    } catch (error) {
        console.error('Error exporting report:', error);
        return { success: false, message: 'Failed export' };
    }
};

const reportsService = {
    getEmployeeReport,
    getAttendanceReport,
    getLeaveReport,
    getPayrollReport,
    getDepartmentReport,
    getOvertimeReport,
    getLateMarksReport,
    getEmployeeMovementReport,
    getLoanAdvanceReport,
    getDepartmentWiseReport,
    exportReport
};

export default reportsService;
